import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Timestamp } from "@angular/fire/firestore";
import { Patient } from "../patient.model";

interface RespiratoryFields {
  /**
   * Whether the patient's respiratory rhythm is irregular (true) or not (false).
   */
  irregularRhythm: boolean;

  /**
   * Whether retractions are present (true) or not (false).
   */
  retractionsPresent: boolean;

  /**
   * The quality of the patient's respirations.
   *
   * Options: dyspnea, tachypnea, bradypnea, or apnea.
   */
  quality: string;
  qualityOther: string;

  // Lung Sounds

  /**
   * The sound of the right upper lobe.
   *
   * Options: coarse rhonchi, crackles/rales, wheezes expiratory, wheezes inspiratory,
   * or other - describe.
   */
  lungSoundsRul: string;
  lungSoundsRulOther: string;

  /**
   * The sound of the right lower lobe.
   *
   * Options: coarse rhonchi, crackles/rales, wheezes expiratory, wheezes inspiratory,
   * or other - describe.
   */
  lungSoundsRll: string;
  lungSoundsRllOther: string;

  /**
   * The sound of the right middle lobe.
   *
   * Options: coarse rhonchi, crackles/rales, wheezes expiratory, wheezes inspiratory,
   * or other - describe.
   */
  lungSoundsRml: string;
  lungSoundsRmlOther: string;

  /**
   * The sound of the left upper lobe.
   *
   * Options: coarse rhonchi, crackles/rales, wheezes expiratory, wheezes inspiratory,
   * or other - describe.
   */
  lungSoundsLul: string;
  lungSoundsLulOther: string;

  /**
   * The sound of the left lower lobe.
   *
   * Options: coarse rhonchi, crackles/rales, wheezes expiratory, wheezes inspiratory,
   * or other - describe.
   */
  lungSoundsLll: string;
  lungSoundsLllOther: string;

  /**
   * Whether a cough is present (true) or not (false).
   */
  cough: boolean;

  /**
   * Whether the assessment is unable to assess (true) or is not unable to assess (false)
   **/
  abilityToAssess: boolean;

  /**
   * The type of cough, if any. This should only have a value if the "cough" field is true.
   *
   * Options: productive, nonproductive, or dry.
   */
  coughDescription: string;

  /**
   * The characteristics of the product produced by the patient's cough.
   *
   * Options: scant, moderate, large, purulent, blood tinged, black, green, yellow,
   * thick, thin, and tenacious.
   */
  productiveDescription: string[];

  increasedWorkOfBreathing: string;

  trachPresent: boolean;
  trachSize: string;
  innerCannulaChanged: boolean;
  innerCannulaChangedDetails: string;
  trachTiesChanged: boolean;
  trachTiesChangedDetails: string;
  suction: string;
  otherFindings: string;
}

export type RespiratoryFormGroup = FormGroup<ControlsOf<RespiratoryFields>>;

export const RespiratoryAssessmentHelpers: AssessmentHelpers<RespiratoryAssessment, Patient> = {
  createFormGroup: createRespiratoryFormGroup,
  toPDF: respiratoryAssessmentToPDF,
  formToObject: formToRespiratoryAssessment,
  initialState: {
    irregularRhythm: false,
    retractionsPresent: false,
    quality: '',
    qualityOther: '',
    lungSoundsRul: '',
    lungSoundsRulOther: '',
    lungSoundsRll: '',
    lungSoundsRllOther: '',
    lungSoundsRml: '',
    lungSoundsRmlOther: '',
    lungSoundsLul: '',
    lungSoundsLulOther: '',
    lungSoundsLll: '',
    lungSoundsLllOther: '',
    cough: false,
    abilityToAssess: false,
    coughDescription: '',
    productiveDescription: [],
    increasedWorkOfBreathing: '',
    trachPresent: false,
    trachSize: '',
    innerCannulaChanged: false,
    innerCannulaChangedDetails: '',
    trachTiesChanged: false,
    trachTiesChangedDetails: '',
    suction: '',
    otherFindings: '',
  },
};

/**
 * Holds information about the patient's respiratory assessment.
 */
export class RespiratoryAssessment {
  irregularRhythm: boolean;
  retractionsPresent: boolean;
  quality: string;
  qualityOther: string;
  lungSoundsRul: string;
  lungSoundsRulOther: string;
  lungSoundsRll: string;
  lungSoundsRllOther: string;
  lungSoundsRml: string;
  lungSoundsRmlOther: string;
  lungSoundsLul: string;
  lungSoundsLulOther: string;
  lungSoundsLll: string;
  lungSoundsLllOther: string;
  cough: boolean;
  abilityToAssess: boolean;
  coughDescription: string;
  productiveDescription: string[];
  increasedWorkOfBreathing: string;
  trachPresent: boolean;
  trachSize: string;
  innerCannulaChanged: boolean;
  innerCannulaChangedDetails: string;
  trachTiesChanged: boolean;
  trachTiesChangedDetails: string;
  suction: string;
  otherFindings: string;

  constructor(data: Partial<RespiratoryFields>) {
    const is = RespiratoryAssessmentHelpers.initialState;
    this.irregularRhythm = data.irregularRhythm ?? is['irregularRhythm'];
    this.retractionsPresent = data.retractionsPresent ?? is['retractionsPresent'];
    this.quality = data.quality || is['quality'];
    this.qualityOther = data.qualityOther || is['qualityOther'];
    this.lungSoundsRul = data.lungSoundsRul || is['lungSoundsRul'];
    this.lungSoundsRulOther = data.lungSoundsRulOther || is['lungSoundsRulOther'];
    this.lungSoundsRll = data.lungSoundsRll || is['lungSoundsRll'];
    this.lungSoundsRllOther = data.lungSoundsRllOther || is['lungSoundsRllOther'];
    this.lungSoundsRml = data.lungSoundsRml || is['lungSoundsRml'];
    this.lungSoundsRmlOther = data.lungSoundsRmlOther || is['lungSoundsRmlOther'];
    this.lungSoundsLul = data.lungSoundsLul || is['lungSoundsLul'];
    this.lungSoundsLulOther = data.lungSoundsLulOther || is['lungSoundsLulOther'];
    this.lungSoundsLll = data.lungSoundsLll || is['lungSoundsLll'];
    this.lungSoundsLllOther = data.lungSoundsLllOther || is['lungSoundsLllOther'];
    this.cough = data.cough ?? is['cough'];
    this.abilityToAssess = data.abilityToAssess || is['abilityToAssess'];
    this.coughDescription = data.coughDescription || is['coughDescription'];
    this.productiveDescription = data.productiveDescription || is['productiveDescription'];
    this.increasedWorkOfBreathing = data.increasedWorkOfBreathing || is['increasedWorkOfBreathing'];
    this.trachPresent = data.trachPresent ?? is['trachPresent'];
    this.trachSize = data.trachSize || is['trachSize'];
    this.innerCannulaChanged = data.innerCannulaChanged ?? is['innerCannulaChanged'];
    this.innerCannulaChangedDetails = data.innerCannulaChangedDetails || is['innerCannulaChangedDetails'];
    this.trachTiesChanged = data.trachTiesChanged ?? is['trachTiesChanged'];
    this.trachTiesChangedDetails = data.trachTiesChangedDetails || is['trachTiesChangedDetails'];
    this.suction = data.suction || is['suction'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}


function createRespiratoryFormGroup(respiratoryAssessment?: RespiratoryAssessment) {
  const data = respiratoryAssessment || new RespiratoryAssessment(RespiratoryAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<RespiratoryFields>>({
    irregularRhythm: new FormControl<boolean | null>(data.irregularRhythm),
    retractionsPresent: new FormControl<boolean | null>(data.retractionsPresent),
    quality: new FormControl<string | null>(data.quality),
    qualityOther: new FormControl<string | null>(data.qualityOther),
    lungSoundsRul: new FormControl<string | null>(data.lungSoundsRul),
    lungSoundsRulOther: new FormControl<string | null>(data.lungSoundsRulOther),
    lungSoundsRll: new FormControl<string | null>(data.lungSoundsRll),
    lungSoundsRllOther: new FormControl<string | null>(data.lungSoundsRllOther),
    lungSoundsRml: new FormControl<string | null>(data.lungSoundsRml),
    lungSoundsRmlOther: new FormControl<string | null>(data.lungSoundsRmlOther),
    lungSoundsLul: new FormControl<string | null>(data.lungSoundsLul),
    lungSoundsLulOther: new FormControl<string | null>(data.lungSoundsLulOther),
    lungSoundsLll: new FormControl<string | null>(data.lungSoundsLll),
    lungSoundsLllOther: new FormControl<string | null>(data.lungSoundsLllOther),
    cough: new FormControl<boolean | null>(data.cough),
    abilityToAssess: new FormControl<boolean | null>(data.abilityToAssess),
    coughDescription: new FormControl<string | null>(data.coughDescription),
    productiveDescription: new FormControl<string[]>(data.productiveDescription),
    increasedWorkOfBreathing: new FormControl<string | null>(data.increasedWorkOfBreathing),
    trachPresent: new FormControl<boolean | null>(data.trachPresent),
    trachSize: new FormControl<string | null>(data.trachSize),
    innerCannulaChanged: new FormControl<boolean | null>(data.innerCannulaChanged),
    innerCannulaChangedDetails: new FormControl<string | null>(data.innerCannulaChangedDetails),
    trachTiesChanged: new FormControl<boolean | null>(data.trachTiesChanged),
    trachTiesChangedDetails: new FormControl<string | null>(data.trachTiesChangedDetails),
    suction: new FormControl<string | null>(data.suction),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function respiratoryAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: RespiratoryAssessment, patient: Patient) {
  pdf.addSectionTitle("Respiratory");
  pdf.pdf.text("Respiratory Pattern", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Rhythm", assessment.irregularRhythm ? "Yes" : "No", "Quality", assessment.quality);
  if (patient.isPediatric()) {
    pdf.addTwoColumnText("Retractions Present", assessment.retractionsPresent ? "Yes" : "No", "", "");
  }
  pdf.currY += 10;
  pdf.pdf.text("Lung Sounds", pdf.margin, pdf.currY)
  pdf.currY += 20;
  pdf.addTwoColumnText("RUL", assessment.lungSoundsRul, "RML", assessment.lungSoundsRml);
  pdf.addTwoColumnText("RLL", assessment.lungSoundsRll, "LUL", assessment.lungSoundsLul);
  pdf.addOneColumnText("LLL", assessment.lungSoundsLll);
  pdf.currY += 10;
  pdf.pdf.text("Breathing", pdf.margin, pdf.currY)
  pdf.currY += 20;
  pdf.addOneColumnText("Increased Work of Breathing", assessment.increasedWorkOfBreathing);
  pdf.currY += 10;
  pdf.pdf.text("Cough", pdf.margin, pdf.currY)
  pdf.currY += 20;
  pdf.addOneColumnText("Cough Present", assessment.cough ? "Yes" : "No");
  pdf.currY += 10;
  pdf.pdf.text("Trach", pdf.margin, pdf.currY)
  pdf.currY += 20;
  pdf.addOneColumnText("Trach Present?", assessment.trachPresent ? "Yes" : "No");
  if (assessment.trachPresent) {
    pdf.addTwoColumnText("Trach Size", assessment.trachSize, "Inner Cannula Changed?", assessment.innerCannulaChanged ? "Yes" : "No");
    if (assessment.innerCannulaChanged) {
      pdf.addOneColumnText("Inner Cannula Changed Details", assessment.innerCannulaChangedDetails);
    }
    pdf.addTwoColumnText("Trach Ties Changed?", assessment.trachTiesChanged ? "Yes" : "No", "", "");
    if (assessment.trachTiesChanged) {
      pdf.addOneColumnText("Trach Ties Changed Details", assessment.trachTiesChangedDetails);
    }
  }
  pdf.currY += 10;
  pdf.addOneColumnText("Trach Suction", assessment.suction);
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
  pdf.currY += 20;
}

function formToRespiratoryAssessment(fg: RespiratoryFormGroup) {

  let quality = fg.controls.quality?.value;
  // Append other text to form values that are "Other"
  if (quality === 'Other') {
    quality = quality + ' - ' + fg.controls.qualityOther?.value;
  }

  // Lung sounds
  let rul = fg.controls.lungSoundsRul?.value;
  let rll = fg.controls.lungSoundsRll?.value;
  let rml = fg.controls.lungSoundsRml?.value;
  let lul = fg.controls.lungSoundsLul?.value;
  let lll = fg.controls.lungSoundsLll?.value;

  // Append other text to form values that are "Other"
  if (rul === 'Other') {
    rul = rul + ' - ' + fg.controls.lungSoundsRulOther?.value;
  }
  if (rll === 'Other') {
    rll = rll + ' - ' + fg.controls.lungSoundsRllOther?.value;
  }
  if (rml === 'Other') {
    rml = rml + ' - ' + fg.controls.lungSoundsRmlOther?.value;
  }
  if (lul === 'Other') {
    lul = lul + ' - ' + fg.controls.lungSoundsLulOther?.value;
  }
  if (lll === 'Other') {
    lll = lll + ' - ' + fg.controls.lungSoundsLllOther?.value;
  }

  return new RespiratoryAssessment({
    irregularRhythm: fg.controls.irregularRhythm.value!,
    retractionsPresent: fg.controls.retractionsPresent.value!,
    quality: quality!,
    lungSoundsRul: rul!,
    lungSoundsRll: rll!,
    lungSoundsRml: rml!,
    lungSoundsLul: lul!,
    lungSoundsLll: lll!,
    cough: fg.controls.cough.value!,
    coughDescription: fg.controls.coughDescription.value!,
    productiveDescription: fg.controls.productiveDescription.value!,
    trachPresent: fg.controls.trachPresent.value!,
    trachSize: fg.controls.trachSize.value!,
    innerCannulaChanged: fg.controls.innerCannulaChanged.value!,
    innerCannulaChangedDetails: fg.controls.innerCannulaChangedDetails.value!,
    trachTiesChanged: fg.controls.trachTiesChanged.value!,
    trachTiesChangedDetails: fg.controls.trachTiesChangedDetails.value!,
    suction: fg.controls.suction.value!,
    otherFindings: fg.controls.otherFindings.value!,
    increasedWorkOfBreathing: fg.controls.increasedWorkOfBreathing.value!,
    abilityToAssess: fg.controls.abilityToAssess.value!,
  });
}